"use client"

import * as React from "react"
import { useDelete } from "react-admin"
import { toast } from "sonner"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog"
import { ServiceLocation } from "../ServiceLocationsList"
import { Loader2 } from "lucide-react"

interface DeleteServiceLocationProps {
  location: ServiceLocation
  onSuccess: () => void
  trigger: React.ReactNode
}

export function DeleteServiceLocation({
  location,
  onSuccess,
  trigger,
}: DeleteServiceLocationProps) {
  const [deleteRecord, { isLoading }] = useDelete()
  const [open, setOpen] = React.useState(false)

  const handleDelete = async () => {
    try {
      await deleteRecord("route-areas", { id: location.id })
      toast.success(`Service location "${location.name}" deleted successfully`)
      onSuccess()
      setOpen(false)
    } catch (error) {
      toast.error("Error deleting service location")
    }
  }

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild onClick={() => setOpen(true)}>
        {trigger}
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Are you sure?</AlertDialogTitle>
          <AlertDialogDescription>
            This will permanently delete this location "{location.name}".
            This action cannot be undone.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={handleDelete}
            disabled={isLoading}
            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
}